import gsap from 'gsap';
import { defineElement } from '~/utils/gw-utils';

class TeamMember extends HTMLElement {
  constructor() {
    super();
    this.summary = this.querySelector('summary');
    this.details = this.querySelector('details');
    this.modal = this.querySelector('.team__member-modal');
    this.close = this.querySelector('.team__member-modal-close');

    // Keep track of the original parent and next sibling
    this.originalParent = this.modal.parentNode;
    this.originalNextSibling = this.modal.nextSibling;
    this.eventListeners = [];
    this.focusableElements = null;
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
  }

  connectedCallback() {
    // Set initial state of the modal
    gsap.set(this.modal, { autoAlpha: 0, width: 0, height: 0 });

    // Add ARIA attributes to the summary element (trigger)
    this.summary.setAttribute('aria-haspopup', 'dialog');
    this.summary.setAttribute('role', 'button');

    this.summary.addEventListener('click', (event) => {
      event.preventDefault();

      document.querySelectorAll('details[open]').forEach((openDetails) => {
        if (openDetails && openDetails !== this.details) {
          const openTeamMember = openDetails.closest('team-member');
          if (openTeamMember && typeof openTeamMember.closeModal === 'function') {
            openTeamMember.closeModal();
          } else {
            openDetails.removeAttribute('open');
          }
        }
      });

      this.openModal();
    });

    this.close.addEventListener('click', () => {
      this.closeModal();
    });
  }

  openModal() {
    this.details.setAttribute('open', '');

    // Make image width and height the same
    const teamMemberImage = this.querySelector('.team__member-image-wrapper');
    const teamMemberModalImage = this.querySelector('.team__member-modal-image-wrapper');
    const imageWidth = teamMemberImage.offsetWidth;
    const imageHeight = teamMemberImage.offsetHeight;

    teamMemberModalImage.style.width = `${imageWidth}px`;
    teamMemberModalImage.style.height = `${imageHeight}px`;

    // Get the position of the modal before moving it
    const rect = this.modal.getBoundingClientRect();

    // Move the modal to the body
    document.body.appendChild(this.modal);

    // Add ARIA attributes for accessibility
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');

    // To wide boolean
    let toWide = false;

    // Get the modal's and viewport's dimensions
    let windowSidePadding = 48;
    let modalWidth = 300;
    let modalPadding = 24;
    const viewportWidth = window.innerWidth;
    const summaryWidth = this.summary.offsetWidth;

    if (window.matchMedia('(min-width: 564px)').matches) {
      modalWidth = 420;
      modalPadding = 30;
    }

    if (window.matchMedia('(min-width: 768px)').matches) {
      modalWidth = 380;
      modalPadding = 40;
    }

    if (window.matchMedia('(min-width: 992px)').matches) {
      modalWidth = 450;
      modalPadding = 40;
    }

    if (window.matchMedia('(min-width: 1200px)').matches) {
      modalWidth = 590;
      modalPadding = 40;
    }

    // Find out if the modal is too wide for the viewport
    if (rect.left + modalWidth > viewportWidth - windowSidePadding) {
      toWide = true;
    }

    // Set the modal's position to fixed at the same position
    if (toWide) {
      this.modal.classList.add('team__member-modal--wide');
      Object.assign(this.modal.style, {
        position: 'fixed',
        top: `${rect.top}px`,
        right: `${viewportWidth - rect.right - summaryWidth - modalPadding}px`,
        left: 'auto',
        zIndex: 20,
        direction: 'rtl',
      });
    } else {
      Object.assign(this.modal.style, {
        position: 'fixed',
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        right: 'auto',
        zIndex: 20,
      });
    }

    // Animate the modal to open
    gsap.to(this.modal, {
      autoAlpha: 1,
      width: 'auto',
      height: 'auto',
      duration: 0.5,
      ease: 'power3.out',
      onComplete: () => {
        // Set up focus trap after animation completes
        this.setupFocusTrap();
      },
    });

    const currentScroll = window.scrollY;
    const scrollListener = () => {
      const top = currentScroll - window.scrollY;
      this.modal.style.top = `${rect.top + top}px`;
    };

    const resizeListener = () => {
      this.closeModal();
    };

    const keydownListener = (e) => {
      if (e.key === 'Escape') {
        this.closeModal();
      } else if (e.key === 'Tab') {
        this.handleTabKey(e);
      }
    };

    document.addEventListener('scroll', scrollListener);
    window.addEventListener('resize', resizeListener);
    document.addEventListener('keydown', keydownListener);

    this.eventListeners.push({ type: 'scroll', listener: scrollListener });
    this.eventListeners.push({ type: 'resize', listener: resizeListener });
    this.eventListeners.push({ type: 'keydown', listener: keydownListener });
  }

  setupFocusTrap() {
    // Get all focusable elements within the modal
    this.focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (this.focusableElements.length > 0) {
      this.firstFocusableElement = this.focusableElements[0];
      this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];

      // Focus the first element (preferably the close button)
      const closeButton = this.modal.querySelector('.team__member-modal-close');
      if (closeButton) {
        closeButton.focus();
      } else {
        this.firstFocusableElement.focus();
      }
    }
  }

  handleTabKey(e) {
    // If no focusable elements, do nothing
    if (!this.focusableElements || this.focusableElements.length === 0) return;

    // SHIFT + TAB
    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    }
    // TAB
    else {
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  closeModal() {
    this.eventListeners.forEach(({ type, listener }) => {
      if (type === 'resize') {
        window.removeEventListener(type, listener);
      } else {
        document.removeEventListener(type, listener);
      }
    });

    // Clear event listeners array
    this.eventListeners = [];

    gsap.to(this.modal, {
      autoAlpha: 0,
      width: 0,
      height: 0,
      duration: 0.5,
      ease: 'power3.out',
      onComplete: () => {
        this.details.removeAttribute('open');

        // Remove the styles we added
        this.modal.style.position = '';
        this.modal.style.top = '';
        this.modal.style.left = '';
        this.modal.style.zIndex = '';

        // Remove ARIA attributes
        this.modal.removeAttribute('role');
        this.modal.removeAttribute('aria-modal');

        // Move the modal back to its original place
        this.originalParent.insertBefore(this.modal, this.originalNextSibling);

        // Return focus to the trigger element
        this.summary.focus();
      },
    });
  }
}

defineElement('team-member', TeamMember);
